import React, { useEffect, useRef, useState } from "react";
import {
  animatedAlertCheck,
  animatedAlertCross,
} from "../../assets/icons/SvgIconList";
import ICButton from "./ICButton";
import UseTranslationHook from "../../hooks/UseTranslationHook";
import { ALERT_TYPE } from "../../utils/AppEnumerations";
import { cn } from "../../lib";

export interface ISweetAlertOptions {
  message: string;
  title?: string;
  duration?: number;
  noClose?: boolean;
}

export interface ICAnimatedAlertProps {
  message: string;
  title?: string;
  duration?: number;
  type?: "success" | "error";
  onClose?: () => void;
  noClose?: boolean;
}

const ICAnimatedAlert: React.FC<ICAnimatedAlertProps> = ({
  message,
  title,
  type = ALERT_TYPE.Success,
  duration = 5000,
  onClose,
  noClose = false,
}) => {
  const [drawn, setDrawn] = useState(false);
  const [visible, setVisible] = useState(true);
  const { t } = UseTranslationHook();
  const alertRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setDrawn(false);
    const drawTimer = setTimeout(() => setDrawn(true), 200);

    if (!noClose) {
      const closeTimer = setTimeout(() => {
        setVisible(false);
        if (onClose) onClose();
      }, duration);
      return () => {
        clearTimeout(drawTimer);
        clearTimeout(closeTimer);
      };
    } else {
      return () => {
        clearTimeout(drawTimer);
      };
    }
  }, [duration, type, onClose, noClose]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        alertRef.current &&
        !alertRef.current.contains(event.target as Node)
      ) {
        setVisible(false);
        if (onClose) onClose();
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [onClose]);

  if (!visible) return null;

  const renderIcon = () => {
    switch (type) {
      case ALERT_TYPE.Success:
        return (
          <div className={cn(`checkmark w-20 h-20`, { draw: drawn })}>
            {animatedAlertCheck}
          </div>
        );
      case ALERT_TYPE.Error:
        return (
          <div className={cn(`checkmark w-20 h-20`, { draw: drawn })}>
            {animatedAlertCross}
          </div>
        );
      default:
        return null;
    }
  };

  const handleOkClick = () => {
    setVisible(false);
    if (onClose) onClose();
  };

  return (
    <div className={`custom-alert`} ref={alertRef}>
      <div className="flex items-center flex-col justify-center">
        {renderIcon()}
        {title && <h3 className="mt-5 text-lg">{title}</h3>}
        <p className="mt-5">{message}</p>
        <ICButton onClick={handleOkClick} className="mt-5 uppercase">
          {t("ok")}
        </ICButton>
      </div>
    </div>
  );
};

export default ICAnimatedAlert;
